// src/components/management/BookingDetails.jsx
import React, { useState } from "react";
import PropTypes from "prop-types";
import { Tabs, Tab, Box } from "@mui/material";
import { format, parseISO, isValid } from "date-fns";
import "../../../styles/components/management/Calendar/BookingDetails.css";

const fmtDate = (iso, pattern = "dd MMM yyyy") => {
  if (!iso) return "–";
  const dt = parseISO(iso);
  return isValid(dt) ? format(dt, pattern) : iso;
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tab-panel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  value: PropTypes.number.isRequired,
  index: PropTypes.number.isRequired,
};

const DetailRow = ({ label, value, isLink = false, linkPrefix = "" }) => (
  <div className="detail-row">
    <span className="detail-label">{label}</span>
    <span className="detail-value">
      {value != null && value !== "" ? (
        isLink ? (
          <a href={`${linkPrefix}${value}`} target="_blank" rel="noopener noreferrer">
            {value}
          </a>
        ) : (
          value
        )
      ) : (
        "–"
      )}
    </span>
  </div>
);

DetailRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.any,
  isLink: PropTypes.bool,
  linkPrefix: PropTypes.string,
};

const BookingDetails = ({ booking, property, onClose }) => {
  const [tabIndex, setTabIndex] = useState(0);
  if (!booking) return null;

  const handleTabChange = (e, newIndex) => setTabIndex(newIndex);
  const guest = booking.guest || {};

  return (
    <div className="booking-details-overlay">
      <div className="booking-details-modal">
        <button className="close-button" onClick={onClose}>
          &times;
        </button>

        {/* Property Header */}
        {property?.PROPERTY_IMAGE && (
          <div
            className="property-header"
            style={{ backgroundImage: `url(${property.PROPERTY_IMAGE})` }}
          >
            <div className="property-header-overlay" />
            <div className="property-header-content">
              <h3 className="property-name">{property.PROPERTY_NAME}</h3>
              <div className="property-address">
                {property.PROPERTY_ADDRESS}, {property.PROPERTY_POST_CODE}
              </div>
            </div>
          </div>
        )}

        <Tabs
          value={tabIndex}
          onChange={handleTabChange}
          aria-label="Booking detail tabs"
        >
          <Tab label="Summary" id="tab-0" />
          <Tab label="Financials" id="tab-1" />
          <Tab label="Property" id="tab-2" />
          <Tab label="Guest" id="tab-3" />
          <Tab label="Extras" id="tab-4" />
        </Tabs>

        {/* Summary */}
        <TabPanel value={tabIndex} index={0}>
          <DetailRow label="Booking ID" value={booking.API_REFERENCE || booking.ID} />
          <DetailRow label="Status" value={booking.STATUS} />
          <DetailRow label="Arrival" value={fmtDate(booking.ARRIVAL)} />
          <DetailRow label="Departure" value={fmtDate(booking.DEPARTURE)} />
          <DetailRow label="Adults" value={booking.NUM_ADULT} />
          <DetailRow label="Children" value={booking.NUM_CHILD} />
          <DetailRow label="Platform" value={booking.PLATFORM} />
        </TabPanel>

        {/* Financials */}
        <TabPanel value={tabIndex} index={1}>
          <DetailRow label="Price" value={booking.PRICE} />
          <DetailRow label="Deposit" value={booking.DEPOSIT} />
          <DetailRow label="Commission" value={booking.COMMISSION} />
          <DetailRow label="Tax" value={booking.TAX} />
          <DetailRow label="Invoice ID" value={booking.PLATFORM_INVOICE_ID} />
          <DetailRow label="Stripe Token" value={booking.STRIPE_TOKEN} />
          <DetailRow label="Sub-Status" value={booking.SUB_STATUS} />
        </TabPanel>

        {/* Property / Unit */}
        <TabPanel value={tabIndex} index={2}>
          <DetailRow label="Property ID" value={property?.PROPERTY_ID} />
          <DetailRow label="Beds24 Property ID" value={booking.PROPERTY_BEDS24_ID} />
          <DetailRow label="Room ID" value={booking.ROOM_ID} />
          <DetailRow label="Rate Description" value={booking.RATE_DESCRIPTION} />
        </TabPanel>

        {/* Guest */}
        <TabPanel value={tabIndex} index={3}>
          <DetailRow label="First Name" value={guest.FIRST_NAME} />
          <DetailRow label="Last Name" value={guest.LAST_NAME} />
          <DetailRow label="Email" value={guest.EMAIL} />
          <DetailRow label="Phone" value={guest.PHONE} />
          <DetailRow label="Address" value={guest.ADDRESS} />
          <DetailRow label="City" value={guest.CITY} />
          <DetailRow label="Postcode" value={guest.POSTCODE} />
          <DetailRow label="Country" value={guest.COUNTRY} />
          <DetailRow label="Company" value={guest.COMPANY} />
          <DetailRow label="Language" value={guest.LANGUAGE} />
        </TabPanel>

        {/* Extras */}
        <TabPanel value={tabIndex} index={4}>
          <DetailRow
            label="Booking Time"
            value={fmtDate(booking.BOOKING_TIME, "dd MMM yyyy, HH:mm")}
          />
          <DetailRow
            label="Modified Time"
            value={fmtDate(booking.MODIFIED_TIME, "dd MMM yyyy, HH:mm")}
          />
          <DetailRow
            label="Cancel Time"
            value={fmtDate(booking.CANCEL_TIME, "dd MMM yyyy, HH:mm")}
          />
          <DetailRow
            label="Platform Message"
            value={booking.PLATFORM_CONFIRMATION_MESSAGE}
          />
          <DetailRow label="Notes" value={booking.NOTES} />
          <DetailRow
            label="Pre-Check Docs"
            value={booking.CA_PRE_CHECKIN_DOCUM}
          />
          <DetailRow
            label="Pre-Arrival Link"
            value={booking.CA_PRE_ARRIVAL_LINK}
            isLink
            linkPrefix=""
          />
          <DetailRow
            label="Pre-Check T&C"
            value={booking.CA_PRE_CHECKIN_TC}
          />
          <DetailRow
            label="Pre-Check Deposit"
            value={booking.CA_PRE_CHECKIN_DEPOS}
          />
        </TabPanel>
      </div>
    </div>
  );
};

BookingDetails.propTypes = {
  booking: PropTypes.object,
  property: PropTypes.object,
  onClose: PropTypes.func.isRequired,
};

export default BookingDetails;
